import React, { useMemo } from 'react';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { mergeClasses } from '@fluentui/react-components';
import { SingleResult } from './single-result';
import { getComparisonDatePeriod } from './get-comparison-date-period';
import { type SummarizePluginResult } from '../../types';
import { useNormalizedPerfMetrics } from './use-normalized-perf-metrics';

export type InsightsSummarizePluginResultProps = {
  className?: string;
  summarizePluginResult: SummarizePluginResult;
  isReportingInsights15Enabled?: boolean,
};

export const InsightsSummarizePluginResult: React.FC<InsightsSummarizePluginResultProps> = (props) => {
  const { className, summarizePluginResult, isReportingInsights15Enabled } = props;

  const i18n = useI18nContext();
  const perfMetrics = useNormalizedPerfMetrics({ isReportingInsights15Enabled, summarizePluginResult });

  const headerText = useMemo(() => {
    const campaignName: string | undefined = summarizePluginResult?.Parameters?.CampaignName;
    if (campaignName) {
      return i18n?.getString(_TL_('{{campaignName}} performance overview'), { campaignName });
    }

    return i18n?.getString(_TL_('Campaign performance overview'));
  }, [i18n, summarizePluginResult]);

  const currency = useMemo(() => summarizePluginResult?.Result?.accountCurrency, [summarizePluginResult]);

  const comparisonPeriod = useMemo(() => isReportingInsights15Enabled && getComparisonDatePeriod({ i18n, summarizePluginResult }), [i18n, summarizePluginResult]);

  if (!summarizePluginResult) {
    return null;
  }

  return (
    <div className={mergeClasses('summarize-plugin-results', className)}>
      <div><strong>{headerText}</strong></div>
      {isReportingInsights15Enabled
      && (
        <p className="summarize-comparison-period">
          {comparisonPeriod}
        </p>
      )}
      <SingleResult
        className="impressions"
        content={perfMetrics?.impressions?.currentValue}
        title={i18n?.getString(_TL_('Impressions:'))}
        impact={perfMetrics?.impressions?.impact}
        percentShift={perfMetrics?.impressions?.percentShift}
      />
      <SingleResult
        className="clicks"
        content={perfMetrics?.clicks?.currentValue}
        title={i18n?.getString(_TL_('Clicks:'))}
        impact={perfMetrics?.clicks?.impact}
        percentShift={perfMetrics?.clicks?.percentShift}
      />
      <SingleResult
        className="ctr"
        content={perfMetrics?.ctr?.currentValue}
        title={i18n?.getString(_TL_('CTR:'))}
        type="percent"
        impact={perfMetrics?.ctr?.impact}
        percentShift={perfMetrics?.ctr?.percentShift}
      />
      <SingleResult
        className="spend"
        content={perfMetrics?.spend?.currentValue}
        title={i18n?.getString(_TL_('Spend:'))}
        type="currency"
        currency={currency}
        impact={perfMetrics?.spend?.impact}
        percentShift={perfMetrics?.spend?.percentShift}
      />
      <SingleResult
        className="conversions"
        content={perfMetrics?.conversions?.currentValue}
        title={i18n?.getString(_TL_('Conversions:'))}
        impact={perfMetrics?.conversions?.impact}
        percentShift={perfMetrics?.conversions?.percentShift}
      />
      <SingleResult
        className="cpa"
        content={perfMetrics?.cpa?.currentValue}
        title={i18n?.getString(_TL_('CPA:'))}
        type="currency"
        impact={perfMetrics?.cpa?.impact}
        percentShift={perfMetrics?.cpa?.percentShift}
      />
      <SingleResult
        className="roas"
        content={perfMetrics?.roas?.currentValue}
        title={i18n?.getString(_TL_('ROAS:'))}
        type="percent"
        impact={perfMetrics?.roas?.impact}
        percentShift={perfMetrics?.roas?.percentShift}
      />
      <SingleResult
        className="average-cpc"
        content={perfMetrics?.averageCPC?.currentValue}
        title={i18n?.getString(_TL_('Average CPC:'))}
        type="currency"
        impact={perfMetrics?.averageCPC?.impact}
        percentShift={perfMetrics?.averageCPC?.percentShift}
      />
      <SingleResult
        className="average-cpm"
        content={perfMetrics?.averageCPM?.currentValue}
        title={i18n?.getString(_TL_('Average CPM:'))}
        type="currency"
        impact={perfMetrics?.averageCPM?.impact}
        percentShift={perfMetrics?.averageCPM?.percentShift}
      />
      <SingleResult
        className="impression-share"
        content={perfMetrics?.impressionShare?.currentValue}
        title={i18n?.getString(_TL_('Impression share:'))}
        type="percent"
        impact={perfMetrics?.impressionShare?.impact}
        percentShift={perfMetrics?.impressionShare?.percentShift}
      />
      <SingleResult
        className="unique-reach"
        content={perfMetrics?.uniqueReach?.currentValue}
        title={i18n?.getString(_TL_('Unique reach:'))}
        impact={perfMetrics?.uniqueReach?.impact}
        percentShift={perfMetrics?.uniqueReach?.percentShift}
      />
      <SingleResult
        className="average-cpv"
        content={perfMetrics?.averageCPV?.currentValue}
        title={i18n?.getString(_TL_('Average CPV:'))}
        type="currency"
        impact={perfMetrics?.averageCPV?.impact}
        percentShift={perfMetrics?.averageCPV?.percentShift}
      />
      <SingleResult
        className="video-views"
        content={perfMetrics?.videoViews?.currentValue}
        title={i18n?.getString(_TL_('Video views:'))}
        impact={perfMetrics?.videoViews?.impact}
        percentShift={perfMetrics?.videoViews?.percentShift}
      />
      <SingleResult
        className="video-completion-rate"
        content={perfMetrics?.videoCompletionRate?.currentValue}
        title={i18n?.getString(_TL_('Video completion rate:'))}
        type="percent"
        impact={perfMetrics?.videoCompletionRate?.impact}
        percentShift={perfMetrics?.videoCompletionRate?.percentShift}
      />
      <SingleResult
        className="video-views-at-25-percent"
        content={perfMetrics?.videoViewsAt25Percent?.currentValue}
        title={i18n?.getString(_TL_('Video views at 25%:'))}
        impact={perfMetrics?.videoViewsAt25Percent?.impact}
        percentShift={perfMetrics?.videoViewsAt25Percent?.percentShift}
      />
      <SingleResult
        className="video-views-at-50-percent"
        content={perfMetrics?.videoViewsAt50Percent?.currentValue}
        title={i18n?.getString(_TL_('Video views at 50%:'))}
        impact={perfMetrics?.videoViewsAt50Percent?.impact}
        percentShift={perfMetrics?.videoViewsAt50Percent?.percentShift}
      />
      <SingleResult
        className="video-views-at-75-percent"
        content={perfMetrics?.videoViewsAt75Percent?.currentValue}
        title={i18n?.getString(_TL_('Video views at 75%:'))}
        impact={perfMetrics?.videoViewsAt75Percent?.impact}
        percentShift={perfMetrics?.videoViewsAt75Percent?.percentShift}
      />
      <SingleResult
        className="installs"
        content={perfMetrics?.installs?.currentValue}
        title={i18n?.getString(_TL_('Installs:'))}
        impact={perfMetrics?.installs?.impact}
        percentShift={perfMetrics?.installs?.percentShift}
      />
      <SingleResult
        className="downloads"
        content={perfMetrics?.downloads?.currentValue}
        title={i18n?.getString(_TL_('Downloads:'))}
        impact={perfMetrics?.downloads?.impact}
        percentShift={perfMetrics?.downloads?.percentShift}
      />
      <SingleResult
        className="average-cpi"
        content={perfMetrics?.averageCPI?.currentValue}
        title={i18n?.getString(_TL_('Average CPI:'))}
        type="currency"
        impact={perfMetrics?.averageCPI?.impact}
        percentShift={perfMetrics?.averageCPI?.percentShift}
      />
    </div>
  );
};
